import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import VerifiedBadge from "../components/VerifiedBadge";
import { formatTeachingStructure } from "../utils/teachingFormat";

const avgR = a => a?.length ? +(a.reduce((x,y)=>x+y,0)/a.length).toFixed(1) : 0;

const styles = `
  @keyframes fadeInUp {
    from { opacity:0; transform:translateY(16px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes pulse-dot {
    0%, 100% { opacity:1; transform:scale(1); }
    50%       { opacity:0.5; transform:scale(0.8); }
  }
  .fade-in   { animation: fadeInUp 0.4s ease both; }
  .fade-in-2 { animation: fadeInUp 0.4s ease 0.1s both; }
  .fade-in-3 { animation: fadeInUp 0.4s ease 0.2s both; }
  .fade-in-4 { animation: fadeInUp 0.4s ease 0.3s both; }
  .fade-in-5 { animation: fadeInUp 0.4s ease 0.4s both; }
  .teacher-name {
    background: linear-gradient(90deg, #ffffff 0%, #f97316 60%, #ffffff 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 5s linear infinite;
  }
  .stat-card { transition: border-color 0.25s, box-shadow 0.25s, transform 0.2s; }
  .stat-card:hover { border-color: #f97316 !important; box-shadow: 0 0 20px rgba(249,115,22,0.2); transform: translateY(-2px); }
  .section-card { transition: border-color 0.25s ease; }
  .section-card:hover { border-color: #f9731633 !important; }
  .glow-input:focus { border-color: #f97316 !important; box-shadow: 0 0 0 3px rgba(249,115,22,0.15) !important; outline: none !important; }
  .orange-btn { transition: box-shadow 0.2s, transform 0.15s; }
  .orange-btn:hover { box-shadow: 0 4px 20px rgba(249,115,22,0.5); transform: translateY(-1px); }
  .wa-btn { transition: box-shadow 0.2s, transform 0.15s; }
  .wa-btn:hover { box-shadow: 0 4px 20px rgba(37,211,102,0.4); transform: translateY(-1px); }
  .ghost-btn { transition: border-color 0.2s, color 0.2s, box-shadow 0.2s; }
  .ghost-btn:hover { border-color: #f97316 !important; color: #f97316 !important; box-shadow: 0 0 12px rgba(249,115,22,0.2); }
  .star-btn { transition: color 0.15s, filter 0.15s, transform 0.1s; display: inline-block; }
  .star-btn:hover { transform: scale(1.2); }
  .star-btn.active { filter: drop-shadow(0 0 8px #f59e0b); }
  .avail-dot { animation: pulse-dot 2s ease infinite; display: inline-block; width:7px; height:7px; border-radius:50%; background:#22c55e; }
  .review-card { transition: border-color 0.2s, box-shadow 0.2s; }
  .review-card:hover { border-color: #f9731633 !important; box-shadow: 0 4px 16px rgba(249,115,22,0.08); }
  .subject-tag { transition: all 0.2s; }
  .subject-tag:hover { background: rgba(249,115,22,0.12) !important; border-color: #f97316 !important; color: #f97316 !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #0a0a0a; }
  ::-webkit-scrollbar-thumb { background: #f97316; border-radius: 4px; }
  .tp-stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-top:20px; }
  .tp-content-pad { padding: 20px 20px 0; }
  @media(max-width:600px) {
    .tp-stats-grid { grid-template-columns:repeat(2,1fr); }
    .tp-profile-header { flex-direction: column !important; gap: 14px !important; }
    .tp-unlock-btn { width: 100% !important; }
    .tp-wa-btn { width: 100% !important; }
    .tp-ghost-btn { width: 100% !important; }
    .tp-hero-pad { padding: 16px 14px 20px !important; }
    .tp-content-pad { padding: 14px 14px 0 !important; }
    .tp-back-row { margin-bottom: 16px !important; }
    .tp-profile-img { width:68px !important; height:68px !important; }
    .tp-profile-avatar { width:68px !important; height:68px !important; font-size:22px !important; }
  }
  @media(max-width:480px) {
    .tp-stats-grid { grid-template-columns:repeat(2,1fr); }
    .tp-stat-val { font-size:13px !important; }
  }
`;

export default function TeacherProfile() {
  const { id }   = useParams();
  const { user } = useAuth();
  const nav      = useNavigate();
  const location = useLocation();

  const [t,            setT]           = useState(null);
  const [error,        setError]       = useState(false);
  const [reviews,      setReviews]     = useState([]);
  const [myRating,     setMyRating]    = useState(0);
  const [hovered,      setHovered]     = useState(0);
  const [rated,        setRated]       = useState(false);
  const [rErr,         setRErr]        = useState("");
  const [reviewText,   setReviewText]  = useState("");
  const [reviewMsg,    setReviewMsg]   = useState("");
  const [reviewErr,    setReviewErr]   = useState("");
  const [submitting,   setSubmitting]  = useState(false);
  const [alreadySent,  setAlreadySent] = useState(false);

  // ── Contact unlock state ────────────────────────────────────────
  const [unlockStatus, setUnlockStatus] = useState(null);
  // null = loading | "own" | "lead_sent" | "locked"
  const [ownPhone,     setOwnPhone]     = useState(null);
  const [credits,      setCredits]      = useState(0);
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const [unlocking,    setUnlocking]    = useState(false);
  const [unlockErr,    setUnlockErr]    = useState("");
  const [waLink,       setWaLink]       = useState(null);

  // ── Handle Cashfree redirect-back (UPI Intent / mobile fallback) ──
  useEffect(() => {
    if (!id || !user) return;
    const params    = new URLSearchParams(window.location.search);
    const cfPayment = params.get("cf_payment");
    const cfOrderId = params.get("cf_order_id");
    if (cfPayment !== "credits" || !cfOrderId) return;

    window.history.replaceState({}, "", window.location.pathname);

    api.post("/contacts/credits/verify/" + id, { order_id: cfOrderId })
      .then(({ data }) => {
        setUnlockStatus("lead_sent");
        setCredits(data.credits ?? 0);
        if (data.wa_link) setWaLink(data.wa_link);
        window.dispatchEvent(new Event("pp:contacts-updated"));
      })
      .catch(e => setUnlockErr(e.response?.data?.message || "Payment received but verification failed. Contact support."));
  }, [id, user]);

  // ── Load teacher + reviews in parallel, fire view counter ──────
  useEffect(() => {
    if (!id || id === "undefined") { setError(true); return; }
    api.post("/teachers/" + id + "/view").catch(() => {});
    api.get("/teachers/" + id).then(r => setT(r.data)).catch(() => setError(true));
    api.get("/teachers/" + id + "/reviews").then(r => setReviews(r.data)).catch(() => {});
  }, [id]);

  // ── Check unlock status ─────────────────────────────────────────
  useEffect(() => {
    if (!id || id === "undefined") return;
    if (!user) { setUnlockStatus("locked"); return; }

    api.get("/contacts/check/" + id)
      .then(r => {
        if (r.data.own_profile) { setUnlockStatus("own"); setOwnPhone(r.data.phone); return; }
        if (r.data.lead_sent)   { setUnlockStatus("lead_sent"); if (r.data.wa_link) setWaLink(r.data.wa_link); return; }
        setUnlockStatus("locked");
        setCredits(r.data.credits ?? 0);
        setCooldownSecs(r.data.cooldown_seconds ?? 0);
      })
      .catch(() => setUnlockStatus("locked"));
  }, [id, user]);

  // ── Cooldown countdown timer ─────────────────────────────────────
  useEffect(() => {
    if (cooldownSecs <= 0) return;
    const t = setInterval(() => setCooldownSecs(s => s <= 1 ? 0 : s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldownSecs]);

  // ── Student spends 1 credit → gets teacher WhatsApp directly → teacher hidden until platform fee paid ──
  const unlockWhatsApp = async () => {
    if (!user) { nav("/login?redirect=" + encodeURIComponent(location.pathname)); return; }
    setUnlocking(true);
    setUnlockErr("");
    try {
      const { data } = await api.post("/contacts/whatsapp/" + id, {
        student_class: t?.teaching?.[0]?.class || "",
      });
      if (data.lead_sent) {
        setUnlockStatus("lead_sent");
        setCredits(data.credits_remaining ?? 0);
        if (data.wa_link) setWaLink(data.wa_link);
        window.dispatchEvent(new Event("pp:contacts-updated"));
      }
    } catch (e) {
      if (e.response?.status === 429) {
        setCooldownSecs(e.response.data.waitSeconds || 300);
      } else if (e.response?.status === 402) {
        setCredits(0);
      } else {
        setUnlockErr(e.response?.data?.message || "Something went wrong");
      }
    }
    setUnlocking(false);
  };

  // ── Buy 3 credits for ₹29 then auto-unlock ───────────────────────
  const buyCredits = async () => {
    if (!user) { nav("/login?redirect=" + encodeURIComponent(location.pathname)); return; }
    setUnlocking(true);
    setUnlockErr("");
    try {
      const returnUrl = `${window.location.origin}/teacher/${id}?cf_payment=credits`;
      const { data: order } = await api.post("/contacts/credits/order", { return_url: returnUrl });

      const { load }  = await import("@cashfreepayments/cashfree-js");
      const cfMode    = process.env.NODE_ENV === "production" ? "production" : "sandbox";
      const cashfree  = await load({ mode: cfMode });

      const result = await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget:   "_modal",
      });

      if (result.paymentDetails) {
        try {
          const { data: verify } = await api.post("/contacts/credits/verify/" + id, { order_id: order.order_id });
          setUnlockStatus("lead_sent");
          setCredits(verify.credits ?? 0);
          if (verify.wa_link) setWaLink(verify.wa_link);
          window.dispatchEvent(new Event("pp:contacts-updated"));
        } catch {
          setUnlockErr("Payment done but verification failed. Please contact support — your credits will be added.");
        }
      } else if (result.error) {
        setUnlockErr("Payment cancelled or failed. Try again.");
      }
    } catch (e) {
      setUnlockErr(e.response?.data?.message || "Something went wrong. Try again.");
    }
    setUnlocking(false);
  };

  // ── Rating ──────────────────────────────────────────────────────
  const submitRating = async () => {
    if (!myRating) return;
    if (!user) { nav("/login?redirect=" + encodeURIComponent(location.pathname)); return; }
    try {
      const { data } = await api.post("/teachers/" + id + "/rate", { stars: myRating });
      setT(p => ({ ...p, rating_avg: data.rating_avg, ratings: [...(p.ratings||[]), myRating] }));
      setRated(true);
    } catch (e) {
      setRErr(e.response?.data?.message || "Rating failed");
    }
  };

  // ── Review ──────────────────────────────────────────────────────
  const submitReview = async () => {
    if (!user) { nav("/login?redirect=" + encodeURIComponent(location.pathname)); return; }
    if (!reviewText.trim()) { setReviewErr("Please write something"); return; }
    const words = reviewText.trim().split(/\s+/).length;
    if (words > 200) { setReviewErr("Review must be 200 words or less"); return; }
    setSubmitting(true);
    setReviewErr("");
    try {
      await api.post("/teachers/" + id + "/review", { text: reviewText.trim() });
      setReviewMsg("Review submitted! Teacher will verify and approve it.");
      setReviewText("");
      setAlreadySent(true);
    } catch (e) {
      setReviewErr(e.response?.data?.message || "Submission failed");
    }
    setSubmitting(false);
  };

  const wordCount   = reviewText.trim() ? reviewText.trim().split(/\s+/).length : 0;
  const ratingLabel = ["","Poor","Fair","Good","Very Good","Excellent"];

  if (error && !t) return (
    <div style={{ textAlign:"center", padding:80, color:"#475569", background:"#0a0a0a", minHeight:"100vh" }}>
      <style>{styles}</style>
      <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
      <div style={{ fontSize:15, color:"#ef4444", marginBottom:20 }}>Failed to load teacher profile.</div>
      <button onClick={() => window.location.reload()}
        style={{ background:"#f97316", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
        Try Again
      </button>
    </div>
  );

  if (!t) return (
    <div style={{ textAlign:"center", padding:80, color:"#475569", background:"#0a0a0a", minHeight:"100vh" }}>
      <style>{styles}</style>
      <div style={{ fontSize:40, marginBottom:16 }}>⏳</div>
      <div style={{ fontSize:15, color:"#64748b" }}>Loading profile...</div>
    </div>
  );

  const rating   = t.rating_avg || avgR(t.ratings);
  const initials = (t.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();

  const metaTitle = t
    ? `${t.name} – Home Tutor in ${t.city || "Kanpur"} | PadhaiPass`
    : "Teacher Profile | PadhaiPass";
  const metaDesc = t
    ? `${t.name} teaches ${t.subjects?.slice(0,3).join(", ") || "multiple subjects"} in ${t.area || t.city || "Kanpur"}. Fee: ₹${t.fees_per_hour || "—"}/month. Contact directly on WhatsApp via PadhaiPass.`
    : "View tutor profile on PadhaiPass.";

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", paddingBottom:60, color:"#f1f5f9" }}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={`https://www.padhaipass.com/teacher/${t._id}`} />
        <meta property="og:title"       content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image"       content={t?.photo_url || "https://www.padhaipass.com/logo.png"} />
        <meta property="og:url"         content={`https://www.padhaipass.com/teacher/${t._id}`} />
        <meta property="og:type"        content="profile" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image"       content={t?.photo_url || "https://www.padhaipass.com/logo.png"} />
        {/* Person + AggregateRating schema — enables star ratings in Google search results */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": t.name,
          "jobTitle": "Home Tutor",
          "description": metaDesc,
          "image": t.photo_url || "https://www.padhaipass.com/logo.png",
          "url": `https://www.padhaipass.com/teacher/${t._id}`,
          "worksFor": { "@type": "Organization", "name": "PadhaiPass" },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": t.area || t.city || "Kanpur",
            "addressRegion": "Uttar Pradesh",
            "addressCountry": "IN"
          },
          "knowsAbout": t.subjects || [],
          ...(rating > 0 && t.ratings?.length > 0 ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": rating,
              "ratingCount": t.ratings.length,
              "bestRating": 5,
              "worstRating": 1
            }
          } : {})
        })}</script>
      </Helmet>
      <style>{styles}</style>

      {/* ── HERO HEADER ── */}
      <div className="tp-hero-pad" style={{ background:"linear-gradient(180deg, #111111 0%, #0a0a0a 100%)", borderBottom:"1px solid #111827", padding:"20px 20px 24px" }}>
        <div style={{ maxWidth:720, margin:"0 auto" }}>

          {/* Top nav */}
          <div className="tp-back-row" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
            <div style={{ fontSize:18, fontWeight:800 }}>
              <span style={{ color:"#fff" }}>Padhai</span>
              <span style={{ color:"#f97316" }}>Pass</span>
            </div>
            <button onClick={() => nav("/search")} className="ghost-btn"
              style={{ background:"transparent", border:"1px solid #1e293b", color:"#64748b", cursor:"pointer", fontSize:13, padding:"7px 16px", borderRadius:20 }}>
              ← Back
            </button>
          </div>

          {/* Profile info */}
          <div className="fade-in" style={{ display:"flex", gap:18, alignItems:"flex-start" }}>
            <div style={{ position:"relative", flexShrink:0 }}>
              {t.photo_url ? (
                <img src={t.photo_url} alt={t.name}
                  style={{ width:84, height:84, borderRadius:16, objectFit:"cover", border:"2px solid #f9731433" }}
                  onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
              ) : (
                <div style={{ width:84, height:84, borderRadius:16, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:28 }}>
                  {initials}
                </div>
              )}
              {t.available && (
                <div style={{ position:"absolute", bottom:-4, right:-4, width:20, height:20, background:"#22c55e", borderRadius:"50%", border:"3px solid #0a0a0a" }} />
              )}
            </div>

            <div style={{ flex:1, minWidth:0 }}>
              <h1 className="teacher-name" style={{ fontSize:"clamp(24px,4vw,34px)", fontWeight:900, margin:"0 0 6px", letterSpacing:"-0.5px", color:"#ffffff", textShadow:"0 0 18px rgba(255,255,255,0.15)", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                {t.name}
                {t.verified && <VerifiedBadge />}
              </h1>
              {t.referred_by_name && (
                <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#22c55e12", border:"1px solid #22c55e33", borderRadius:20, padding:"3px 10px", marginBottom:6, width:"fit-content" }}>
                  <span style={{ color:"#22c55e", fontSize:11 }}>✓</span>
                  <span style={{ fontSize:11, color:"#22c55e", fontWeight:600 }}>Joined via PadhaiPass referral</span>
                </div>
              )}
              <div style={{ fontSize:13, color:"#e2e8f0", fontWeight:500, marginBottom:6, textShadow:"0 0 10px rgba(255,255,255,0.12)" }}>
                📍 {t.area}{t.city ? ", " + t.city : ""}
              </div>
              {/* Credential tagline — degree + experience only */}
              {(() => {
                const edu = t.education;
                const parts = [];
                if (edu?.degree_name) {
                  const status = edu.degree_status === "pursuing" ? " (Pursuing)" : edu.degree_status === "completed" ? " (Completed)" : "";
                  parts.push(edu.degree_name + status);
                }
                if (!parts.length) return null;
                return (
                  <div style={{ fontSize:12, color:"#94a3b8", marginBottom:8, fontWeight:500 }}>
                    {parts.join(" · ")}
                  </div>
                );
              })()}
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                {/* Teaching chips — structured compact format */}
                {(() => {
                  const { compact } = formatTeachingStructure(t.teaching, t.classes, t.subjects);
                  if (compact) return compact.split(" | ").map((part, i) => (
                    <span key={i} style={{ background:"#f9731615", color:"#f97316", fontSize:12, padding:"3px 10px", borderRadius:6, border:"1px solid #f9731630", fontWeight:600 }}>
                      {part}
                    </span>
                  ));
                  // fallback
                  if (t.teaching?.length > 0) return t.teaching.map((entry, i) => (
                    <span key={i} style={{ background:"#f9731615", color:"#f97316", fontSize:12, padding:"3px 10px", borderRadius:6, border:"1px solid #f9731630", fontWeight:600 }}>
                      {entry.class}
                    </span>
                  ));
                  if (t.classes?.length > 0) return (Array.isArray(t.classes) ? t.classes : [t.classes]).map((c, i) => (
                    <span key={i} style={{ background:"#f9731615", color:"#f97316", fontSize:12, padding:"3px 10px", borderRadius:6, border:"1px solid #f9731630", fontWeight:600 }}>
                      {c}
                    </span>
                  ));
                  return null;
                })()}
                <span style={{ background:t.available?"#22c55e15":"#ef444415", color:t.available?"#22c55e":"#ef4444", fontSize:12, padding:"3px 12px", borderRadius:6, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>
                  {t.available && <span className="avail-dot"></span>}
                  {t.available ? "Available" : "Not Available"}
                </span>
              </div>
              <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} style={{ fontSize:14, color:s<=Math.round(rating)?"#f59e0b":"#1e293b" }}>★</span>
                ))}
                <span style={{ fontSize:13, fontWeight:700, color:"#f59e0b" }}>{rating||"—"}</span>
                <span style={{ fontSize:12, color:"#64748b" }}>({t.ratings?.length||0} ratings)</span>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="fade-in-2 tp-stats-grid">
            {[
              [t.fees_per_hour ? "₹"+t.fees_per_hour+"/mo" : "—", "per month"],
              [t.experience != null && t.experience !== "" ? t.experience+"yr" : "—", "experience"],
              [t.schedule_type === "regular" ? "6 days/wk" : t.schedule_type === "alternate" ? "3 days/wk" : "—", "schedule"],
              [reviews.length||0,              "reviews"],
            ].map(([val,label]) => (
              <div key={label} className="stat-card"
                style={{ background:"#0f0f0f", borderRadius:12, padding:"12px 8px", textAlign:"center", border:"1px solid #1a1a2e" }}>
                <div style={{ fontSize:15, fontWeight:800, color:"#f97316" }}>{val}</div>
                <div style={{ fontSize:10, color:"#64748b", marginTop:3, fontWeight:500, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="tp-content-pad" style={{ maxWidth:720, margin:"0 auto", padding:"20px 20px 0" }}>

        {/* ── CONTACT SECTION ── */}
        <div className="fade-in-3 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
          <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:12 }}>
🎓 Book a Free Demo with {t.name?.split(" ")[0]}
          </div>

          {/* Teacher not currently listing — no contact allowed */}
          {!t.listing_active && unlockStatus !== "own" && (
            <div style={{ textAlign:"center", padding:"14px 16px", background:"#ef444410", borderRadius:10, border:"1px solid #ef444430", fontSize:13, color:"#ef4444", lineHeight:1.6 }}>
              ⚠️ This teacher is not currently accepting new students.
            </div>
          )}

          {/* Loading */}
          {t.listing_active && unlockStatus === null && (
            <div style={{ textAlign:"center", padding:20, color:"#475569", fontSize:13 }}>
              Checking access...
            </div>
          )}

          {/* Own profile */}
          {t.listing_active && unlockStatus === "own" && (
            <div>
              <div style={{ fontSize:13, color:"#475569", marginBottom:14, padding:"10px 14px", background:"#f9731610", borderRadius:8, border:"1px solid #f9731625" }}>
                👤 This is your profile. Students see a WhatsApp contact button here.
              </div>
              <div style={{ background:"#0a0a0a", borderRadius:10, padding:"14px 16px", border:"1px solid #1e293b" }}>
                <div style={{ fontSize:11, color:"#64748b", marginBottom:4, fontWeight:500, textTransform:"uppercase", letterSpacing:0.5 }}>📞 Your Phone</div>
                <div style={{ fontSize:22, fontWeight:800, letterSpacing:2, color:"#f1f5f9" }}>{ownPhone || "Not set"}</div>
              </div>
            </div>
          )}

          {/* Not logged in */}
          {t.listing_active && unlockStatus === "locked" && !user && (
            <div style={{ textAlign:"center", padding:"8px 0 16px" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
              <div style={{ fontSize:14, color:"#64748b", marginBottom:16, lineHeight:1.6 }}>
                Login to contact this teacher
              </div>
              <button onClick={() => nav("/login?redirect=" + encodeURIComponent(location.pathname))} className="orange-btn"
                style={{ width:"100%", padding:13, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, cursor:"pointer", fontWeight:700 }}>
                Login to Continue →
              </button>
            </div>
          )}

          {/* Locked — logged in */}
          {t.listing_active && unlockStatus === "locked" && user && (
            <div>

              {/* Error */}
              {unlockErr && (
                <div style={{ color:"#ef4444", fontSize:13, marginBottom:12, padding:"10px 14px", background:"#ef444415", borderRadius:8, border:"1px solid #ef444430" }}>
                  ⚠ {unlockErr}
                </div>
              )}

              {/* Teacher cannot use this */}
              {user?.role === "teacher" ? (
                <div style={{ textAlign:"center", padding:"14px 16px", background:"#f9731610", borderRadius:10, border:"1px solid #f9731630", fontSize:13, color:"#f97316" }}>
                  👨‍🏫 You are registered as a teacher.<br />
                  <span style={{ color:"#e2e8f0", fontSize:12 }}>Students use this to contact tutors.</span>
                </div>
              ) : cooldownSecs > 0 ? (
                <div style={{ textAlign:"center", padding:"14px", background:"#0a0a0a", borderRadius:10, border:"1px solid #1e293b", color:"#64748b", fontSize:14 }}>
                  ⏱ Wait {Math.floor(cooldownSecs/60)}m {cooldownSecs%60}s before next contact
                </div>
              ) : credits > 0 ? (
                <>
                  <div style={{ background:"#f9731610", border:"1px solid #f9731630", borderRadius:8, padding:"9px 14px", marginBottom:10, fontSize:12, color:"#fb923c", lineHeight:1.6 }}>
                    ℹ️ You will get the teacher's WhatsApp number directly. Chat with them and fix a <strong>free demo class</strong> at your convenience.
                  </div>
                  <button onClick={unlockWhatsApp} disabled={unlocking} className="orange-btn"
                    style={{ width:"100%", padding:14, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:15, cursor:unlocking?"not-allowed":"pointer", fontWeight:700, opacity:unlocking?0.7:1 }}>
                    {unlocking ? "Please wait..." : "📲 Get WhatsApp & Book Free Demo — 1 Credit"}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize:14, color:"#f1f5f9", fontWeight:600, textAlign:"center", marginBottom:12, lineHeight:1.7 }}>
                    You've used all free credits.<br />
                    <span style={{ color:"#e2e8f0", fontWeight:400, fontSize:13 }}>Get 3 more demo requests for ₹29</span>
                  </div>
                  <button onClick={buyCredits} disabled={unlocking} className="orange-btn"
                    style={{ width:"100%", padding:13, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:15, cursor:unlocking?"not-allowed":"pointer", fontWeight:700, opacity:unlocking?0.7:1 }}>
                    {unlocking ? "Processing..." : "Get 3 Contacts — ₹29"}
                  </button>
                  <div style={{ textAlign:"center", fontSize:11, color:"#64748b", marginTop:8 }}>
                    Instant · Secure via Cashfree
                  </div>
                </>
              )}
            </div>
          )}

          {/* Demo confirmed */}
          {unlockStatus === "lead_sent" && (
            <div style={{ padding:"10px 0 4px" }}>
              <div style={{ textAlign:"center", marginBottom:14 }}>
                <div style={{ fontSize:36, marginBottom:8 }}>🎉</div>
                <div style={{ fontWeight:700, fontSize:15, color:"#22c55e", marginBottom:6 }}>WhatsApp Number Unlocked!</div>
                <div style={{ fontSize:13, color:"#94a3b8", lineHeight:1.7 }}>
                  Chat with the teacher directly and fix a <strong style={{color:"#facc15"}}>free demo class</strong> at your time.
                </div>
              </div>

              {waLink ? (
                <button
                  onClick={async () => {
                    // Confirm the demo — hides teacher + creates real lead in admin
                    try {
                      await api.post("/contacts/confirm-demo/" + id);
                    } catch { /* best-effort — still open WhatsApp */ }
                    window.open(waLink, "_blank", "noreferrer");
                  }}
                  style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", padding:14, background:"#25D366", color:"#fff", borderRadius:10, fontSize:15, fontWeight:700, border:"none", cursor:"pointer", boxSizing:"border-box", marginBottom:10 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp →
                </button>
              ) : (
                <div style={{ background:"#22c55e10", border:"1px solid #22c55e30", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#86efac", marginBottom:10, textAlign:"center" }}>
                  ✓ Demo confirmed — teacher will contact you on WhatsApp soon
                </div>
              )}

              <div style={{ background:"#3b82f610", border:"1px solid #3b82f630", borderRadius:10, padding:"9px 14px", fontSize:12, color:"#93c5fd", textAlign:"center" }}>
                💬 Need help? Contact PadhaiPass support — we'll assist you directly.
              </div>
            </div>
          )}
        </div>

        {/* Teaching structure */}
        {(() => {
          const { detailed } = formatTeachingStructure(t.teaching, t.classes, t.subjects);
          if (!detailed.length) return null;
          return (
            <div className="fade-in-2 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:12 }}>📘 Teaching</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {detailed.map((item, i) => (
                  <div key={i} style={{ fontSize:13, color:"#e2e8f0", lineHeight:1.6 }}>
                    <span style={{ color:"#f97316", fontWeight:700, marginRight:6 }}>•</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Education / Credentials ── */}
        {t.education && (t.education.degree_name || t.education.school_10_board || t.education.school_12_board) && (() => {
          const edu = t.education;
          const rows = [];

          if (edu.school_10_board || edu.school_10_name)
            rows.push({
              label: "Class 10",
              value: [edu.school_10_name, edu.school_10_board, edu.school_10_year].filter(Boolean).join(" · "),
            });

          if (edu.school_12_board || edu.school_12_name)
            rows.push({
              label: "Class 12",
              value: [edu.school_12_name, edu.school_12_board, edu.school_12_year].filter(Boolean).join(" · "),
            });

          if (edu.degree_name) {
            const status = edu.degree_status === "completed"
              ? (edu.degree_year ? `Completed ${edu.degree_year}` : "Completed")
              : edu.degree_status === "pursuing"
              ? (edu.degree_sem ? `Pursuing · ${edu.degree_sem} Year` : "Pursuing")
              : "";
            rows.push({
              label: edu.degree_name,
              value: [edu.college_name, status].filter(Boolean).join(" · "),
            });
          }

          if (!rows.length) return null;
          return (
            <div className="fade-in-2 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", marginBottom:12 }}>🎓 Education</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {rows.map((r, i) => (
                  <div key={i} style={{ fontSize:13, color:"#e2e8f0", lineHeight:1.6 }}>
                    <span style={{ color:"#f97316", fontWeight:700, marginRight:6 }}>•</span>
                    <span style={{ fontWeight:600 }}>{r.label}</span>
                    {r.value && <span style={{ color:"#64748b", marginLeft:6 }}>{r.value}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Experience */}
        {t.experience > 0 && (
          <div className="fade-in-2 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", marginBottom:12 }}>🏆 Experience</div>
            <div style={{ fontSize:14, color:"#e2e8f0" }}>
              {t.experience} {t.experience === 1 ? "year" : "years"} of teaching experience
            </div>
          </div>
        )}

        {/* Bio */}
        {t.bio && (
          <div className="fade-in-2 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:12 }}>📝 About</div>
            <p style={{ fontSize:14, color:"#e2e8f0", lineHeight:1.8, margin:0 }}>{t.bio}</p>
          </div>
        )}

        {/* ── RATE THIS TEACHER ── */}
        <div className="fade-in-3 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
          <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:6 }}>Rate this Teacher</div>
          <div style={{ fontSize:12, color:"#64748b", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ background:"#f9731615", color:"#f97316", fontSize:10, padding:"2px 8px", borderRadius:6, fontWeight:600 }}>STUDENT LOGIN REQUIRED</span>
          </div>

          {rated ? (
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:40, marginBottom:10 }}>🎉</div>
              <div style={{ color:"#22c55e", fontSize:15, fontWeight:700 }}>Thanks for your rating!</div>
              <div style={{ color:"#64748b", fontSize:13, marginTop:6 }}>Your feedback helps other students</div>
            </div>
          ) : (
            <>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                <div style={{ display:"flex", gap:8 }}>
                  {[1,2,3,4,5].map(s => (
                    <span key={s}
                      className={"star-btn" + ((hovered||myRating)>=s?" active":"")}
                      onMouseEnter={() => setHovered(s)}
                      onMouseLeave={() => setHovered(0)}
                      onClick={() => setMyRating(s)}
                      style={{ fontSize:42, cursor:"pointer", color:(hovered||myRating)>=s?"#f59e0b":"#1a1a2e", userSelect:"none" }}>
                      ★
                    </span>
                  ))}
                </div>
                {(hovered||myRating) > 0 && (
                  <div style={{ fontSize:13, color:"#f59e0b", fontWeight:600 }}>
                    {ratingLabel[hovered||myRating]}
                  </div>
                )}
              </div>
              {rErr && (
                <div style={{ color:"#ef4444", fontSize:13, marginTop:12, textAlign:"center", background:"#ef444415", padding:8, borderRadius:8 }}>{rErr}</div>
              )}
              <button onClick={submitRating} disabled={!myRating}
                className={myRating ? "orange-btn" : ""}
                style={{ width:"100%", marginTop:16, padding:13, background:myRating?"#f97316":"transparent", color:myRating?"#fff":"#334155", border:myRating?"none":"1px solid #1e293b", borderRadius:10, fontSize:14, cursor:myRating?"pointer":"not-allowed", fontWeight:700, transition:"all .2s" }}>
                {myRating ? "Submit " + myRating + " Star Rating →" : "Select a rating above"}
              </button>
            </>
          )}
        </div>

        {/* ── WRITE A REVIEW ── */}
        <div className="fade-in-4 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, marginBottom:12, border:"1px solid #1a1a2e" }}>
          <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:6 }}>Write a Review</div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:14 }}>
            <span style={{ background:"#f9731610", color:"#f97316", fontSize:10, padding:"2px 8px", borderRadius:6, fontWeight:600, border:"1px solid #f9731625" }}>VERIFIED ONLY</span>
            <span style={{ fontSize:12, color:"#e2e8f0", fontWeight:600 }}>Teacher approves before publishing</span>
          </div>

          {reviewMsg ? (
            <div style={{ background:"#22c55e0f", border:"1px solid #22c55e25", color:"#22c55e", padding:"14px 16px", borderRadius:10, fontSize:13, display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:20 }}>✅</span>{reviewMsg}
            </div>
          ) : alreadySent ? null : !user ? (
            <button onClick={() => nav("/login?redirect=" + encodeURIComponent(location.pathname))} className="orange-btn"
              style={{ width:"100%", padding:13, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, cursor:"pointer", fontWeight:700 }}>
              Login to Write a Review →
            </button>
          ) : user.role !== "student" ? (
            <div style={{ color:"#64748b", fontSize:13, padding:"10px 0" }}>Only students can write reviews.</div>
          ) : (
            <>
              <textarea className="glow-input"
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                placeholder="Share your honest experience — how did this teacher help you? (max 200 words)"
                rows={4}
                style={{ width:"100%", padding:"12px 14px", borderRadius:10, border:"1px solid "+(wordCount>200?"#ef4444":"#1e293b"), background:"#0a0a0a", color:"#f1f5f9", fontSize:13, resize:"none", fontFamily:"inherit", lineHeight:1.7, transition:"border-color .2s, box-shadow .2s" }}
              />
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8, marginBottom:12 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ height:4, width:100, background:"#1a1a2e", borderRadius:4, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:Math.min((wordCount/200)*100,100)+"%", background:wordCount>180?"#ef4444":wordCount>150?"#f59e0b":"#22c55e", borderRadius:4, transition:"width .2s, background .2s" }} />
                  </div>
                  <span style={{ fontSize:11, color:wordCount>200?"#ef4444":"#334155" }}>{wordCount}/200</span>
                </div>
                {wordCount>200 && <span style={{ fontSize:11, color:"#ef4444", fontWeight:600 }}>Too long!</span>}
              </div>
              {reviewErr && (
                <div style={{ color:"#ef4444", fontSize:12, marginBottom:10, padding:"8px 12px", background:"#ef444415", borderRadius:8, border:"1px solid #ef444425" }}>{reviewErr}</div>
              )}
              <button onClick={submitReview} disabled={submitting||wordCount>200||!reviewText.trim()}
                className={(!submitting&&wordCount<=200&&reviewText.trim())?"orange-btn":""}
                style={{ width:"100%", padding:13, background:(!submitting&&wordCount<=200&&reviewText.trim())?"#f97316":"transparent", color:(!submitting&&wordCount<=200&&reviewText.trim())?"#fff":"#334155", border:(!submitting&&wordCount<=200&&reviewText.trim())?"none":"1px solid #1e293b", borderRadius:10, fontSize:14, cursor:(!submitting&&wordCount<=200&&reviewText.trim())?"pointer":"not-allowed", fontWeight:700, transition:"all .2s" }}>
                {submitting ? "Submitting..." : "Submit Review for Approval →"}
              </button>
            </>
          )}
        </div>

        {/* ── APPROVED REVIEWS ── */}
        {reviews.length > 0 && (
          <div className="fade-in-5 section-card" style={{ background:"#0f0f0f", borderRadius:14, padding:18, border:"1px solid #1a1a2e" }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:16 }}>
              Student Reviews
              <span style={{ background:"#f9731615", color:"#f97316", fontSize:11, padding:"2px 10px", borderRadius:6, marginLeft:10, fontWeight:700, textTransform:"none", letterSpacing:0 }}>
                {reviews.length}
              </span>
            </div>
            {reviews.map((r, i) => (
              <div key={r._id} className="review-card"
                style={{ background:"#0a0a0a", borderRadius:12, padding:16, marginBottom:i<reviews.length-1?12:0, border:"1px solid #1a1a2e" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:13, flexShrink:0 }}>
                      {(r.student?.name||"S").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:"#f1f5f9" }}>{r.student?.name||"Student"}</div>
                      <div style={{ fontSize:11, color:"#f97316", fontWeight:500 }}>✓ Verified student</div>
                    </div>
                  </div>
                  <div style={{ fontSize:11, color:"#64748b", background:"#111111", padding:"4px 10px", borderRadius:6, border:"1px solid #1a1a2e" }}>
                    {new Date(r.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                  </div>
                </div>
                <p style={{ fontSize:14, color:"#e2e8f0", margin:0, lineHeight:1.8, borderLeft:"3px solid #f97316", paddingLeft:14 }}>
                  {r.text}
                </p>
              </div>
            ))}
          </div>
        )}

        <div style={{ height:20 }} />
      </div>
    </div>
  );
}